// Axios
import { instance } from './axios.instance';
import { buildTableQuery } from '../shared/tables/tableQuery';
import { uploadDirect } from './directUpload';

export const documentBaseUrl = ({ id, type }) => {
  const idPart = id ? `${id}/` : '';
  if (type.toLowerCase() === 'offer') {
    return `/proposals/offers/` + idPart;
  } else if (type.toLowerCase() === 'opportunity') {
    return `/proposals/opportunities/` + idPart;
  } else if (type.toLowerCase() === 'preview') {
    return `/proposals/templates/${idPart}/previews`;
  } else if (type.toLowerCase() === 'proposalpreview') {
    return `/proposal/${id}/preview`;
  } else if (type.toLowerCase() === 'web_template') {
    // Template du site web : son catalogue de variables n'a que des noms (aucun
    // document rattaché, donc aucune valeur à résoudre).
    return `/proposals/web_templates/` + idPart;
  } else {
    return `/proposals/templates/` + idPart;
  }
};

/* `params` sert au template de site web : son catalogue dépend du type de
   document qu'il habille (opportunité ou offre), choisi dans l'éditeur. */
export const getVariables = async ({ id, type, params }) => {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, value]) => value != null && value !== '')
  ).toString();
  const url = documentBaseUrl({ id, type }) + 'variables' + (query ? `?${query}` : '');
  try {
    const res = await instance.get(url);
    if (res.status === 200) {
      return res.data;
    }
    throw new Error(`GET proposal variables status code ${res.status}`);
  } catch (error) {}
};

// Dossiers + médias de la banque (company_work) du document courant, servis par
// le concern MediaBankServing côté back. Retourne
// [{ id, name, media: [<MediaSerializer>] }] — trois familles : image, gif, vidéo.
export const getMediaBank = async ({ id, type }) => {
  const url = documentBaseUrl({ id, type }) + 'media_bank';
  try {
    const res = await instance.get(url);
    if (res.status === 200) return res.data;
    throw new Error(`GET media bank status code ${res.status}`);
  } catch (error) {
    return [];
  }
};

// Paliers de résolution d'un média déjà posé. Un bloc ne persiste que l'URL du
// palier retenu et l'id du média : rouvrir le sélecteur demande donc de relire
// `variants` ici, plutôt que de recharger toute la banque pour un seul média.
export const getBankMedia = async ({ id, type, mediaId }) => {
  const url = documentBaseUrl({ id, type }) + `media_bank/${mediaId}`;
  try {
    const res = await instance.get(url, { suppressErrorToast: true });
    if (res.status === 200) return res.data;
    return null;
  } catch (error) {
    return null;
  }
};

// Dossiers + formes composées de la banque (company_work) du document courant,
// servis par le concern ShapeBankServing côté back. Retourne
// [{ id, name, general, shapes: [<ShapeSerializer>] }].
export const getShapeBank = async ({ id, type }) => {
  const url = documentBaseUrl({ id, type }) + 'shape_bank';
  try {
    const res = await instance.get(url);
    if (res.status === 200) return res.data;
    throw new Error(`GET shape bank status code ${res.status}`);
  } catch (error) {
    return [];
  }
};

// Enregistre la forme composée courante dans la banque (dossier auto "editor"
// du document, cf. ShapeBankServing#create_shape_bank_item). Pas de route de
// création pour une opportunité/offre : on passe alors par le document Proposal
// qui les porte (même id, cf. postPreview).
const shapeBankPostUrl = (id, type) => {
  if (type === 'opportunity' || type === 'offer') return `/proposal/${id}/shape_bank`;
  if (type === 'web_template') return `/proposals/web_templates/${id}/shape_bank`;
  return `/proposals/templates/${id}/shape_bank`;
};

export const postShapeBankItem = async (id, type, { name, width, height, definition, thumbnailContent, folderId }) => {
  const res = await instance.post(shapeBankPostUrl(id, type), {
    name,
    width,
    height,
    definition,
    thumbnail_content: thumbnailContent,
    folder_id: folderId,
  });
  return res.data;
};

// Modèles de Carte info de la banque (company_work) du document courant, servis
// par le concern CardBankServing côté back. Retourne
// [{ id, name, general, props }] — pas de dossiers, contrairement aux formes.
export const getCardBank = async ({ id, type }) => {
  const url = documentBaseUrl({ id, type }) + 'card_bank';
  try {
    const res = await instance.get(url);
    if (res.status === 200) return res.data;
    throw new Error(`GET card bank status code ${res.status}`);
  } catch (error) {
    return [];
  }
};

// Enregistre la carte courante comme modèle dans la banque. Même détour que la
// banque de formes pour une opportunité/offre, qui n'ont pas de route de
// création propre.
const cardBankPostUrl = (id, type) => {
  if (type === 'opportunity' || type === 'offer') return `/proposal/${id}/card_bank`;
  if (type === 'web_template') return `/proposals/web_templates/${id}/card_bank`;
  return `/proposals/templates/${id}/card_bank`;
};

export const postCardBankItem = async (id, type, { name, props }) => {
  const res = await instance.post(cardBankPostUrl(id, type), { name, props });
  return res.data;
};

// Documents Costing injectables dans une box PDF, servis par le concern
// DocumentPickerServing. Les groupes dépendent du contexte : dossiers de la
// bibliothèque et « hors dossier » partout, plus l'opportunité et l'offre quand
// on édite une proposition. Retourne [{ key, kind, label, color, security }].
export const getDocumentGroups = async ({ id, type }) => {
  const url = documentBaseUrl({ id, type }) + 'document_groups';
  try {
    const res = await instance.get(url);
    if (res.status === 200) return res.data;
    throw new Error(`GET document groups status code ${res.status}`);
  } catch (error) {
    return [];
  }
};

// Documents d'un groupe. `siteTypologies` à false lève le filtre par typologie
// du site (bascule du panel, sans effet sur un modèle).
export const getDocuments = async ({ id, type, group, search, siteTypologies = true }) => {
  const params = new URLSearchParams({ group });
  if (search) params.set('search', search);
  if (!siteTypologies) params.set('site_typologies', 'false');
  const url = documentBaseUrl({ id, type }) + `documents?${params.toString()}`;
  try {
    const res = await instance.get(url);
    if (res.status === 200) return res.data;
    throw new Error(`GET documents status code ${res.status}`);
  } catch (error) {
    return [];
  }
};

// URL du fichier, signée à la demande : elle n'est pas dans la liste pour ne pas
// déclencher une signature S3 par document affiché.
export const getDocumentPreviewUrl = async ({ id, type, documentId }) => {
  const url = documentBaseUrl({ id, type }) + `documents/${documentId}/preview`;
  try {
    const res = await instance.get(url);
    if (res.status === 200) return res.data?.url ?? null;
    return null;
  } catch (error) {
    return null;
  }
};

// Query params issus de la config d'un tableau paramétrable (ex. BPU) : axes
// "donnée" résolus côté backend. Les axes "présentation" (colonnes masquées/
// ordre/largeur) restent côté front et ne sont donc pas transmis.
// `buildTableQuery` vit dans `src/shared/tables/` : le site web construit les
// mêmes URL que le document.

export const getTable = async (
  { id, type },
  tableName = null,
  { split = false, tableConfig = null, force = false, throwOnError = false } = {}
) => {
  const tableToFetch = tableName ? `/${tableName.toLowerCase()}` : '';
  const url =
    documentBaseUrl({ id, type }) +
    'tables' +
    tableToFetch +
    buildTableQuery({ tableName, split, tableConfig, force });
  try {
    // A table may legitimately not exist for a given document type (e.g. an
    // opportunity-scoped table requested on an offer). Treat a 404 as "no data"
    // (caller handles the empty/undefined result) and suppress the global error toast.
    const res = await instance.get(url, { suppressErrorToast: true });
    if (res.status === 200) {
      return res.data;
    }
    throw new Error(`GET tables status code ${res.status}`);
  } catch (error) {
    console.error(error);
    // `throwOnError` : l'appelant distingue une table absente (404, undefined)
    // d'une requête en échec (calcul en cours, erreur serveur, réseau), qu'il
    // ne doit pas prendre pour un tableau vide.
    if (throwOnError && error?.response?.status !== 404) throw error;
  }
};

// Catalogue de colonnes d'un tableau paramétrable, indépendant des données
// (fonctionne en mode template). Alimente le Drawer de configuration + sa preview.
export const getTableSchema = async (
  { id, type },
  tableName,
  { tableConfig = null } = {}
) => {
  const url =
    documentBaseUrl({ id, type }) +
    'tables/' +
    tableName.toLowerCase() +
    buildTableQuery({ tableName, catalog: true, tableConfig });
  try {
    const res = await instance.get(url);
    if (res.status === 200) {
      return res.data;
    }
    throw new Error(`GET table schema status code ${res.status}`);
  } catch (error) {
    console.error(error);
  }
};

export const getTables = async (documentType, id) => {
  const url = documentBaseUrl({ id: id, type: documentType }) + 'tables';
  try {
    const res = await instance.get(url);
    if (res.status === 200) {
      return res.data;
    }
    throw new Error(`GET tables status code ${res.status}`);
  } catch (error) {
    console.error(error);
  }
};

const imagePostUrl = (id, type) =>
  (type === 'template' ? `/proposals/templates/${id}/images` : `/proposal/${id}/image`);

// Le fichier est monté directement au stockage et seul son `signed_id` est
// posté : au-delà de quelques mégaoctets le base64 ne tient plus (corps un
// tiers plus lourd, entièrement en mémoire côté Rails). Rend le payload média
// complet — paliers de résolution compris.
export const postImageDirect = async (id, type, { file, onProgress }) => {
  const signedId = await uploadDirect(file, onProgress);
  const res = await instance.post(imagePostUrl(id, type), {
    filename: file.name,
    signed_id: signedId,
  });
  return res.data;
};

export const postPreview = async (id, type, { filename, content }) => {
  let previewType;
  if (type === 'opportunity' || type === 'offer') {
    previewType = 'proposalPreview';
  } else {
    previewType = 'preview';
  }
  let url = documentBaseUrl({ id: id, type: previewType });
  try {
    const res = await instance.post(url, {
      filename,
      content: /base64,(.+)/.exec(content[0].src)[1],
    });
    return res.data;
  } catch (e) {
    console.error(e);
  }
};

export const getImages = async (currentDocument) => {
  const url = documentBaseUrl(currentDocument) + 'images';

  try {
    const res = await instance.get(url);
    return res.data;
  } catch (e) {}
};
